import type { MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import {
  getLocalizedPath,
  getRouteByKey,
  getTranslatedModuleSlug,
  COMPARISON_SLUGS,
  isRouteAvailable,
  isModuleAvailable,
} from "@/utils/routing";
import { partnerPortalUrl } from "@/utils/partnerPortal";

type Lang = "sk" | "cs" | "da" | "en" | "de";

export interface FooterLink {
  title: string;
  href: string;
  /** Opens in a new tab as a plain <a> instead of a router <Link>. */
  external?: boolean;
  /** Rendered bold/white, e.g. "all comparisons" or "book a call". */
  emphasis?: boolean;
  testId?: string;
  onClick?: (event?: MouseEvent) => void;
}

export interface FooterLinkGroup {
  id: string;
  title: string;
  links: FooterLink[];
}

interface BookDemo {
  href: string;
  onClick: (event?: MouseEvent) => void;
}

/** Route links that only exist in some locales (per `routeTranslations`). */
const optional = (routeKey: string, lang: Lang, title: string): FooterLink[] =>
  isRouteAvailable(routeKey, lang) ? [{ title, href: getRouteByKey(routeKey, lang) }] : [];

/**
 * Footer navigation grouped by what a visitor is looking for. Legal pages are
 * not here: they live once, in the bottom bar (Footer.tsx).
 */
export function useFooterLinks(bookDemo: BookDemo): FooterLinkGroup[] {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as Lang;

  const product: FooterLink[] = [
    { title: t("layout.footer.quickLinks.features"), href: getLocalizedPath("/moduly", lang) },
    { title: t("layout.footer.quickLinks.atlas"), href: getRouteByKey("productAtlas", lang) },
    { title: t("layout.footer.quickLinks.pricing"), href: getLocalizedPath("/cennik", lang) },
    { title: t("layout.footer.quickLinks.videos"), href: getRouteByKey("videos", lang) },
    ...optional("priceCalculator", lang, t("layout.footer.resources.priceCalculator")),
    ...optional("quizHub", lang, t("sections.rateQuiz.footer")),
    ...optional("aiAssistant", lang, t("layout.footer.resources.aiAssistant")),
    // Bank Connect format template page exists only in Danish (bidmio.dk)
    ...(lang === "da" ? [{ title: "Bank Connect", href: "/bank-connect/" }] : []),
  ];

  // Trade landing pages — same per-locale availability filtering as the header dropdown.
  const branches: FooterLink[] = [
    { routeKey: "tradeElectrician", title: t("layout.header.nav.trades.electrician") },
    { routeKey: "tradePlumber", title: t("layout.header.nav.trades.plumber") },
    { routeKey: "tradePainter", title: t("layout.header.nav.trades.painter") },
    { routeKey: "tradeCarpenter", title: t("layout.header.nav.trades.carpenter") },
    { routeKey: "tradeMason", title: t("layout.header.nav.trades.mason") },
    { routeKey: "tradeRoofer", title: t("layout.header.nav.trades.roofer") },
    { routeKey: "tradeSewer", title: t("layout.header.nav.trades.sewer") },
    { routeKey: "tradeGeneralContractor", title: t("layout.header.nav.trades.generalContractor") },
    { routeKey: "tradeService", title: t("layout.header.nav.trades.service") },
    { routeKey: "forAccountants", title: t("layout.header.nav.trades.accountants") },
  ].flatMap(({ routeKey, title }) => optional(routeKey, lang, title));

  const solutions: FooterLink[] = [
    ...optional("constructionSoftware", lang, t("layout.footer.resources.constructionSoftware")),
    ...optional("wholeFirm", lang, t("layout.footer.resources.wholeFirm")),
    ...optional("soloOffer", lang, t("layout.footer.resources.soloOffer")),
    ...(isModuleAvailable("uctovnictvo", lang)
      ? [
          {
            title: t("layout.footer.resources.accounting"),
            href: `${getRouteByKey("modules", lang)}${getTranslatedModuleSlug("uctovnictvo", lang)}/`,
          },
        ]
      : []),
    ...optional("tradeWebsite", lang, t("layout.footer.resources.tradeWebsite")),
    ...optional("constructionFirmWebsite", lang, t("layout.footer.resources.constructionFirmWebsite")),
    ...optional("serviceFirmWebsite", lang, t("layout.footer.resources.serviceFirmWebsite")),
    ...optional("blogWebsiteLeads", lang, t("layout.footer.resources.websiteLeads")),
  ];

  // Comparison pages are locale-specific (Danish competitors on .dk, local
  // ones on .sk/.cz, none on .com); the group hides when the hub is unavailable.
  const comparisons: FooterLink[] = isRouteAvailable("comparisonHub", lang)
    ? [
        ...COMPARISON_SLUGS.filter(({ routeKey }) => isRouteAvailable(routeKey, lang)).map(
          ({ slug, routeKey }) => ({
            title: t(`layout.footer.comparisons.${slug}`),
            href: getRouteByKey(routeKey, lang),
          }),
        ),
        {
          title: `${t("layout.footer.comparisons.hub")} →`,
          href: getRouteByKey("comparisonHub", lang),
          emphasis: true,
        },
      ]
    : [];

  const company: FooterLink[] = [
    { title: t("layout.footer.company.about"), href: getRouteByKey("about", lang) },
    { title: t("layout.header.blog"), href: getRouteByKey("blog", lang) },
    { title: t("layout.footer.quickLinks.partners"), href: getRouteByKey("partners", lang) },
    {
      title: t("layout.footer.quickLinks.partnerLogin"),
      href: partnerPortalUrl(lang, "/login"),
      external: true,
      testId: "footer-partner-login",
    },
    { title: t("layout.footer.resources.supportFaq"), href: getRouteByKey("contact", lang) },
    { title: t("layout.footer.resources.technology"), href: getRouteByKey("technology", lang) },
    {
      title: t("layout.footer.quickLinks.demo"),
      href: bookDemo.href,
      external: true,
      emphasis: true,
      testId: "button-footer-demo",
      onClick: bookDemo.onClick,
    },
  ];

  return [
    { id: "product", title: t("layout.footer.groups.product"), links: product },
    { id: "branches", title: t("layout.header.nav.branches"), links: branches },
    { id: "solutions", title: t("layout.footer.groups.solutions"), links: solutions },
    { id: "comparisons", title: t("layout.footer.comparisons.title"), links: comparisons },
    { id: "company", title: t("layout.footer.company.title"), links: company },
  ].filter((group) => group.links.length > 0);
}
